import React, { useEffect, useState } from "react";
import { MotiView } from "moti";
import { View, StyleSheet } from "react-native";

import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import GradingCenterHeader from "../../components/adminDashboard/GradingCenter/GradingCenterHeader";
import GradingCenterMetrics from "../../components/adminDashboard/GradingCenter/GradingCenterMetrics";
import GradingCenterTable from "../../components/adminDashboard/GradingCenter/GradingCenterTable";
import GradingPanel from "../../components/adminDashboard/GradingCenter/GradingPanel/GradingPanel";
import GradingCenterFilters from "../../components/adminDashboard/GradingCenter/GradingCenterFilters";
import { GradingCenterSubmissionsProvider } from "../../contexts/GradingCenterContext";
import GradingCenterTableFooter from "../../components/adminDashboard/GradingCenter/GradingPanel/GradingCenterTableFooter";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { useGetSubmissionExportFileQuery, useRequestSubmissionExportMutation } from "../../graphql/generated/graphql";
import useFileFetch from "../../hooks/useFileFetch";
import { DownloadSimple } from "@phosphor-icons/react";

export enum ModalState {
  CLOSED = "CLOSED",
  VIEW_ASSIGNMENT_RESPONSE = "VIEW_ASSIGNMENT_RESPONSE",
  VIEW_ASSESSMENT_RESPONSE = "VIEW_ASSESSMENT_RESPONSE",
}

const EXPORT_POLLING_INTERVAL_MS = 3000;

function GradingCenterView() {
  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);

  const [actionResult, setActionResult] = useState<string | null>(null);

  const [updateError, setUpdateError] = useState<string | null>(null);

  const [requestSubmissionExport] = useRequestSubmissionExportMutation();
  const {
    data: submissionExportFileData,
    refetch: fetchLatestSubmissionExport,
    startPolling,
    stopPolling,
  } = useGetSubmissionExportFileQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [exportState, setExportState] = useState<{
    isGenerating: boolean;
    fileURL: string;
    fileId: string | null;
  }>({
    isGenerating: false,
    fileURL: "",
    fileId: null,
  });
  const [pendingExportMetadata, setPendingExportMetadata] = useState<{ previousFileId: string | null } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { fetchAndOpenFile } = useFileFetch();

  const handleDownloadExport = async () => {
    if (exportState.fileURL) {
      await fetchAndOpenFile(exportState.fileURL);
    }
  };

  const handleExportSubmission = async () => {
    try {
      if (exportState.isGenerating) {
        return;
      }

      setExportError(null);
      setExportState((prev) => ({ ...prev, isGenerating: true, fileURL: "", fileId: null }));

      const previousFileId = submissionExportFileData?.getSubmissionExportFile?.data?.id ?? null;
      setPendingExportMetadata({ previousFileId });

      const res = await requestSubmissionExport();
      if (!res?.data?.requestSubmissionExport?.success) {
        throw new Error(res?.data?.requestSubmissionExport?.message || "Failed to start submission export");
      }

      await fetchLatestSubmissionExport();
      stopPolling?.();
      startPolling?.(EXPORT_POLLING_INTERVAL_MS);
    } catch (err) {
      console.error("Error starting submission export:", err);
      setExportError(err instanceof Error ? err.message : "Failed to start submission export");
      setExportState((prev) => ({ ...prev, isGenerating: false }));
      setPendingExportMetadata(null);
      stopPolling?.();
    }
  };

  useEffect(() => {
    if (!pendingExportMetadata) {
      return;
    }

    const response = submissionExportFileData?.getSubmissionExportFile;
    const latestFile = response?.data;

    if (!response?.success || !latestFile?.id) {
      return;
    }

    if (latestFile.id === pendingExportMetadata.previousFileId) {
      return;
    }

    setExportState({
      isGenerating: false,
      fileURL: latestFile.fileURL ?? "",
      fileId: latestFile.id,
    });
    setPendingExportMetadata(null);
    stopPolling?.();
  }, [pendingExportMetadata, submissionExportFileData, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling?.();
    };
  }, [stopPolling]);

  return (
    <AdminLayout>
      <GradingCenterSubmissionsProvider>
        <MotiView
          from={{
            opacity: 0,
            translateY: 20,
            scale: 1,
          }}
          animate={{
            opacity: 1,
            translateY: 0,
            scale: 1,
          }}
          delay={10}
          // @ts-expect-error - transition is not typed
          transition={{ type: "timing", duration: 500 }}>
          <GradingCenterHeader />

          <GradingCenterMetrics />

          <View style={styles.tableContainer}>
            <View style={styles.tableActionRow}>
              <CustomButton
                text={exportState.isGenerating ? "Generating..." : "Export Submission"}
                styleType="primaryLight"
                size="m"
                leftIcon={<DownloadSimple size={24} color="#3E68FE" />}
                onPress={handleExportSubmission}
                disabled={exportState.isGenerating}
                useTranslationText={false}
                style={styles.exportButton}
                textStyle={{ color: "#3E68FE", fontWeight: "700", fontSize: 14 }}
              />
              {exportState.fileURL && !exportState.isGenerating ? (
                <CustomButton
                  text="Download Export"
                  size="s"
                  weight={700}
                  styleType="primaryLight"
                  onPress={handleDownloadExport}
                  useTranslationText={false}
                />
              ) : null}
            </View>
            {exportError && (
              <CustomText text={exportError} size="xs" useTranslationText={false} style={styles.exportErrorText} />
            )}
            <GradingCenterFilters />
            <GradingCenterTable setModalState={setModalState} />
            <GradingCenterTableFooter />
          </View>

          {/* Action Result Feedback */}
          {actionResult && (
            <View style={styles.feedbackContainer}>
              <CustomText text={actionResult} size="s" style={styles.feedbackText} useTranslationText={false} />
            </View>
          )}

          {/* Error Feedback */}
          {updateError && (
            <View style={[styles.feedbackContainer, styles.errorContainer]}>
              <CustomText text={updateError} size="s" style={styles.errorText} useTranslationText={false} />
            </View>
          )}
        </MotiView>

        {/* Grading Panel - Rendered at root level */}
        <GradingPanel
          modalState={modalState}
          setModalState={setModalState}
          setUpdateError={setUpdateError}
          setActionResult={setActionResult}
        />
      </GradingCenterSubmissionsProvider>
    </AdminLayout>
  );
}

export default GradingCenterView;

const styles = StyleSheet.create({
  feedbackContainer: {
    position: "absolute",
    bottom: 20,
    right: 20,
    backgroundColor: "#10B981",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    maxWidth: 300,
  },
  feedbackText: {
    color: "#FFFFFF",
    fontWeight: "500",
  },
  errorContainer: {
    backgroundColor: "#EF4444",
  },
  errorText: {
    color: "#FFFFFF",
    fontWeight: "500",
  },
  tableContainer: {
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  tableActionRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingTop: 16,
    paddingLeft: 16,
    paddingRight: 16,
    height: 64,
    columnGap: 12,
  },
  exportButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#04060F1A",
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    shadowColor: "#000000",
    shadowOpacity: 0.04,
    borderRadius: 8,
  },
  exportErrorText: {
    color: "#EF4444",
    marginLeft: 16,
    marginBottom: 4,
  },
});
